const TONES = {
  on_track: "hsl(var(--healthy))",
  needs_attention: "hsl(var(--watch))",
  delayed: "hsl(var(--at-risk))",
};

const polar = (cx, cy, r, deg) => {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
};

const arc = (cx, cy, r, from, to) => {
  const [x1, y1] = polar(cx, cy, r, from);
  const [x2, y2] = polar(cx, cy, r, to);
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 0 ${to > from ? 0 : 1} ${x2.toFixed(
    2,
  )} ${y2.toFixed(2)}`;
};

/** Half-circle speedometer. `value` and `marker` are on the 0…max scale. */
export const Gauge = ({
  value,
  max = 100,
  tone = "needs_attention",
  caption,
  readout,
  sub,
  marker,
  markerLabel,
  testId,
}) => {
  const colour = TONES[tone] || TONES.needs_attention;
  const clamp = (v) => Math.max(0, Math.min(max, Number(v)));
  const angle = (v) => 180 - (clamp(v) / max) * 180;
  const has = value !== null && value !== undefined && !Number.isNaN(Number(value));
  const deg = has ? angle(value) : 180;
  const [nx, ny] = polar(100, 96, 58, deg);

  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <svg viewBox="0 0 200 118" className="w-full max-w-[200px]" role="img">
        <path d={arc(100, 96, 76, 180, 0)} fill="none" stroke="hsl(var(--border))" strokeWidth="13" />
        {has && (
          <path
            d={arc(100, 96, 76, 180, deg)}
            fill="none"
            stroke={colour}
            strokeWidth="13"
            style={{ transition: "stroke 300ms ease" }}
          />
        )}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const [x1, y1] = polar(100, 96, 68, 180 - f * 180);
          const [x2, y2] = polar(100, 96, 62, 180 - f * 180);
          return (
            <line
              key={f}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="hsl(var(--muted-foreground))"
              strokeWidth="1"
              opacity="0.5"
            />
          );
        })}
        {marker !== null && marker !== undefined && (
          <line
            x1={polar(100, 96, 84, angle(marker))[0]}
            y1={polar(100, 96, 84, angle(marker))[1]}
            x2={polar(100, 96, 68, angle(marker))[0]}
            y2={polar(100, 96, 68, angle(marker))[1]}
            stroke="hsl(var(--foreground))"
            strokeWidth="2"
            strokeDasharray="3 2"
          />
        )}
        {has && (
          <line x1="100" y1="96" x2={nx} y2={ny} stroke="hsl(var(--foreground))" strokeWidth="2.5" />
        )}
        <circle cx="100" cy="96" r="5" fill="hsl(var(--foreground))" />
        <text x="18" y="112" className="font-data" fontSize="9" fill="hsl(var(--muted-foreground))">
          0
        </text>
        <text
          x="170"
          y="112"
          className="font-data"
          fontSize="9"
          fill="hsl(var(--muted-foreground))"
        >
          {max}
        </text>
      </svg>
      <div className="font-data -mt-2 text-2xl font-bold tracking-tight" style={{ color: colour }}>
        {readout}
      </div>
      <div className="label-caps mt-1 text-center">{caption}</div>
      {sub && <div className="mt-0.5 text-center text-[11px] text-muted-foreground">{sub}</div>}
      {markerLabel && (
        <div className="mt-0.5 text-center text-[11px] text-muted-foreground">{markerLabel}</div>
      )}
    </div>
  );
};

export const toneColour = (tone) => TONES[tone] || TONES.needs_attention;
